import React from 'react'
import {
  ListItemButton,
  ListItemText,
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Collapse
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { useTranslation } from 'react-i18next'

type DiscountCodeFormProps = {
  discountCode: string
  setDiscountCode: (value: string) => void
  isDiscountFormVisible: boolean
  setIsDiscountFormVisible: (value: boolean) => void
  validateDiscountCode: (code: string) => void
  discountResult: { isValid?: boolean; rate?: number } | null
  onRemoveDiscount: () => void
}

const DiscountCodeForm: React.FC<DiscountCodeFormProps> = ({
  discountCode,
  setDiscountCode,
  isDiscountFormVisible,
  setIsDiscountFormVisible,
  validateDiscountCode,
  discountResult,
  onRemoveDiscount
}) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        my: 2
      }}
    >
      {discountResult?.isValid ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'rgba(76, 175, 80, 0.08)'
          }}
        >
          <Box display='flex' alignItems='center' gap={1.5}>
            <LocalOfferIcon sx={{ color: 'success.main', fontSize: 24 }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'success.dark'
                }}
              >
                {t('discountCode.applied')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'text.secondary',
                  mt: 0.3
                }}
              >
                {t('discountCode.code')}: {discountCode || ''}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onRemoveDiscount}
            size='small'
            sx={{
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      ) : (
        <>
          <ListItemButton
            sx={{
              px: 2,
              py: 1.5,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.02)'
              }
            }}
            onClick={() => setIsDiscountFormVisible(!isDiscountFormVisible)}
          >
            <LocalOfferIcon
              sx={{ color: 'primary.main', mr: 1.5, fontSize: 22 }}
            />
            <ListItemText
              primary={t('discountCode.haveCode')}
              primaryTypographyProps={{
                sx: {
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: 'text.primary'
                }
              }}
            />
            {isDiscountFormVisible ? (
              <ExpandLess sx={{ color: 'primary.main' }} />
            ) : (
              <ExpandMore sx={{ color: 'primary.main' }} />
            )}
          </ListItemButton>

          <Collapse in={isDiscountFormVisible} timeout='auto' unmountOnExit>
            <Box
              sx={{
                px: 2,
                pb: 2,
                pt: 1,
                bgcolor: 'rgba(0, 0, 0, 0.01)',
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box display='flex' gap={1.5} alignItems='flex-start'>
                <TextField
                  fullWidth
                  placeholder={t('discountCode.enterCode')}
                  variant='outlined'
                  size='small'
                  value={discountCode || ''}
                  onChange={e => setDiscountCode(e.target.value)}
                  onKeyPress={e => {
                    if (e.key === 'Enter' && discountCode?.trim()) {
                      validateDiscountCode(discountCode)
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover fieldset': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />
                <Button
                  variant='contained'
                  onClick={() => validateDiscountCode(discountCode)}
                  disabled={!discountCode?.trim()}
                  sx={{
                    minWidth: '90px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: 1
                    }
                  }}
                >
                  {t('discountCode.apply')}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  )
}

export default DiscountCodeForm
